<template>
  <div>
    <section v-if="isLoading" :class="$style.providersGames">
      <h1 :class="$style.providersGamesTitle">{{ data.providersTitle }}</h1>
      <p :class="$style.providersGamesSubTitle">{{ data.providersSubtitle }}</p>
      <div :class="$style.formSearchProviders">
        <BitSearchInput
          :class="$style.searchInputProviders"
          no-search-errors
          :autofocus="false"
          :no-results-found="isNoResultsFound"
          :placeholder="storeComponents.sortProvider.placeholderInput"
          :input-value="searchStringProviders"
          :no-found-text="storeComponents.search.noFoundText"
          :search-help-text="storeComponents.search.searchHelpText"
          @searchChanged="searchStringProviders = $event"
        />
        <BitSelect
          v-model="currentSort"
          :class="$style.selectedProvider"
          :placeholder="storeComponents.sortProvider.placeholderSort"
          is-no-margin
          variant="filter"
          :options="providerSortOptions"
        >
          <template #appendInputIcon>
            <img src="/images/common/ico_iconSort.svg??url_encode" />
          </template>
        </BitSelect>
      </div>

      <template v-if="!hasSearchBeenMade">
        <template v-if="showTopProvidersList">
          <h2 :class="$style.headingProvider">
            {{ storeComponents.sortProvider.topProviders }}
          </h2>
          <div :class="[$style.providersGamesWrapper, $style.providersTopGamesWrapper]">
            <ProviderCard
              v-for="provider in topProvidersList"
              :key="provider.providerId"
              :provider="provider"
              :data="data"
              :highlight-title="false"
            />
          </div>
        </template>

        <h2 :class="$style.headingProvider">{{ storeComponents.sortProvider.allProviders }}</h2>
        <div :class="$style.providersGamesWrapper">
          <ProviderCard
            v-for="provider in allProvidersList"
            :key="provider.providerId"
            :provider="provider"
            :data="data"
            :highlight-title="false"
          />
        </div>
      </template>

      <template v-else-if="isNoResultsFound">
        <h2 :class="$style.headingProvider">
          {{ storeComponents.sortProvider.topProviders }}
        </h2>
        <div :class="$style.providersGamesWrapper">
          <ProviderCard
            v-for="provider in topProvidersList"
            :key="provider.providerId"
            :provider="provider"
            :data="data"
            :highlight-title="false"
          />
        </div>
      </template>

      <template v-else>
        <h2 :class="$style.headingProvider">
          {{ storeComponents.search.searchResult }}
        </h2>
        <div :class="$style.providersGamesWrapper">
          <ProviderCard
            v-for="provider in providersFound"
            :key="provider.providerId"
            :provider="provider"
            :data="data"
            :highlight-title="true"
          />
        </div>
      </template>
      <div :class="$style.providerSeoBlockText">
        <h2 :class="$style.headingProvider">{{ storeComponents.sortProvider.titleSeo }}</h2>
        <Markdown :class="$style.providerSeoText" :text="visibleText" />

        <BitButton
          v-if="!showFullText"
          :class="$style.providerSeoBlockButton"
          type="secondary"
          width-size="lg"
          @click="toggleText(true)"
        >
          <template #prependIcon>
            <img src="/images/common/chevron-down.svg?url_encode" alt="" />
          </template>
          {{ showMoreTitle }}
        </BitButton>
        <BitButton
          v-else
          :class="[$style.providerSeoBlockButton, { [$style.active]: showFullText }]"
          type="secondary"
          width-size="lg"
          @click="toggleText(false)"
        >
          <template #prependIcon>
            <img src="/images/common/chevron-down.svg?url_encode" alt="" />
          </template>
          {{ showLessTitle }}
        </BitButton>
      </div>
    </section>
    <ProviderCardSkeleton v-else />
  </div>
</template>

<script lang="ts" setup>
import { BitButton, BitSelect, BitSearchInput } from '@bit/ui-kit';
import { computed, onBeforeMount, ref } from 'vue';
import { ProviderCard, ProviderCardSkeleton } from '~/components/lib/providers';
import Markdown from '~/components/markdown.vue';
import { useStoreCountry } from '~/stores/client/country';
import { useStoreSsGames } from '~/stores/softswiss/games';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import { useStoreProviders } from '~/stores/strapi/providers';
import type { ComponentInfoPageBlocksProvidersBlock } from '~/types/infoPages';
import type { IProviderData } from '~/types/providers';
import type { IGamesForProvidersPage, ISsGame } from '~/types/softswiss/ssGames';

interface ProvidersDataExtended extends IProviderData {
  gamesCount: number;
  restricted: boolean;
  imageUrls: ISsGame[];
}

enum enumProviderSort {
  Default = 'default',
  NameAlphabet = 'nameAlphabet',
  NameAlphabetRevers = 'nameAlphabetRevers',
  GamesCountHigh = 'gamesCountHigh',
  GamesCountLow = 'gamesCountLow',
  Rating = 'rating',
}

defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksProvidersBlock>,
    required: true,
  },
});

const storeProviders = useStoreProviders();
const storeSsGames = useStoreSsGames();
const storeCountry = useStoreCountry();
const storeComponents = useStoreComponents();
const storeButtons = useStoreButtonTitles();

const games = ref<IGamesForProvidersPage>({});
const ssProviders = ref<string[]>([]);
const searchStringProviders = ref('');
const isNoResultsFound = ref(false);
const hasSearchBeenMade = ref(false);
const showFullText = ref(false);
const isLoading = ref(false);

const currentSort = ref('Default');
const providerSortOptions = ref({});
const showTopProvidersList = ref(true);

function initializeProvidersExtended() {
  const providersExtended = {} as { [key: string]: ProvidersDataExtended };

  storeProviders.activeList.forEach((provider) => {
    const ssProviderData = ssProviders.value.find(
      (ssProvider) => provider.providerId === ssProvider
    );

    if (!provider.hideOnProvidersPage) {
      providersExtended[provider.providerId] = {
        ...provider,
        gamesCount: games.value[provider.providerId]?.gamesCount || 0,
        restricted: !ssProviderData,
        imageUrls: games.value[provider.providerId]?.games || [],
      };
    }
  });

  return providersExtended;
}

const topProvidersList = computed(() => {
  const providersExtended = initializeProvidersExtended();

  return Object.values(providersExtended)
    .sort((a, b) => {
      const defaultPosition = 99;
      const restrictedSortValue = 100;

      const aGeoPosition =
        a.positionByGeo && a.positionByGeo.some((pos) => pos.geo === storeCountry.country)
          ? // @ts-ignore
            a.positionByGeo.find((pos) => pos.geo === storeCountry.country).position
          : defaultPosition;

      const bGeoPosition =
        b.positionByGeo && b.positionByGeo.some((pos) => pos.geo === storeCountry.country)
          ? // @ts-ignore
            b.positionByGeo.find((pos) => pos.geo === storeCountry.country).position
          : defaultPosition;

      if (a.restricted && !b.restricted) {
        return restrictedSortValue;
      } else if (!a.restricted && b.restricted) {
        return -restrictedSortValue;
      }

      return aGeoPosition - bGeoPosition;
    })
    .slice(0, 18);
});

const allProvidersList = computed(() => {
  const providersExtended = initializeProvidersExtended();

  return Object.values(providersExtended).sort(
    (provider, nextProvider) => nextProvider.gamesCount - provider.gamesCount
  );
});

const showMoreTitle = computed(() => storeButtons.showMoreGames);
const showLessTitle = computed(() => storeButtons.showLessGames);

const providersFound = computed(() => {
  const providersExtended = initializeProvidersExtended();
  const searchString = searchStringProviders.value.trim().toLowerCase();

  return Object.values(providersExtended)
    .map((provider) => {
      const providerTitleLower = provider.providerTitle.toLowerCase();
      const startIndex = providerTitleLower.indexOf(searchString);
      const endIndex = startIndex + searchString.length;

      return {
        ...provider,
        startTitle: provider.providerTitle.substring(0, startIndex),
        boldTitle: provider.providerTitle.substring(startIndex, endIndex),
        endTitle: provider.providerTitle.substring(endIndex),
      };
    })
    .filter((provider) => provider.providerTitle.toLowerCase().includes(searchString))
    .sort((a, b) => {
      if (!a.restricted && b.restricted) {
        return -1;
      } else if (a.restricted && !b.restricted) {
        return 1;
      }
      return 0;
    });
});

const sortProvider = () => {
  providerSortOptions.value = {
    [enumProviderSort.Default]: storeComponents.sortProvider.default,
    [enumProviderSort.NameAlphabet]: storeComponents.sortProvider.nameAlphabet,
    [enumProviderSort.NameAlphabetRevers]: storeComponents.sortProvider.nameAlphabetRevers,
    [enumProviderSort.GamesCountHigh]: storeComponents.sortProvider.gamesCountHigh,
    [enumProviderSort.GamesCountLow]: storeComponents.sortProvider.gamesCountLow,
    [enumProviderSort.Rating]: storeComponents.sortProvider.rating,
  };
};

function applySort(sortType: string) {
  searchStringProviders.value = '';

  allProvidersList.value.sort((a, b) => {
    if (a.restricted && !b.restricted) {
      return 1;
    }
    if (!a.restricted && b.restricted) {
      return -1;
    }

    // TO DO switch
    if (sortType === enumProviderSort.NameAlphabet) {
      return a.providerTitle.localeCompare(b.providerTitle);
    } else if (sortType === enumProviderSort.NameAlphabetRevers) {
      return b.providerTitle.localeCompare(a.providerTitle);
    } else if (sortType === enumProviderSort.GamesCountHigh) {
      return b.gamesCount - a.gamesCount;
    } else if (sortType === enumProviderSort.GamesCountLow) {
      return a.gamesCount - b.gamesCount;
    } else if (sortType === enumProviderSort.Rating) {
      const aGeoPosition =
        a.positionByGeo && a.positionByGeo.some((pos) => pos.geo === storeCountry.country)
          ? // @ts-ignore
            a.positionByGeo.find((pos) => pos.geo === storeCountry.country).position
          : a.position;

      const bGeoPosition =
        b.positionByGeo && b.positionByGeo.some((pos) => pos.geo === storeCountry.country)
          ? // @ts-ignore
            b.positionByGeo.find((pos) => pos.geo === storeCountry.country).position
          : b.position;

      return (aGeoPosition || 0) - (bGeoPosition || 0);
    }
    return 0;
  });

  showTopProvidersList.value = sortType === enumProviderSort.Default;
}

const fullText = storeComponents.sortProvider.textSeo;
const paragraphs = fullText.split('\n\n');

const visibleText = computed(() => {
  return showFullText.value ? fullText : paragraphs.slice(0, 1).join('\n\n');
});

const toggleText = (expand: boolean) => {
  showFullText.value = expand;
};

watch(currentSort, (newSort) => applySort(newSort));

watch(searchStringProviders, (newValue) => {
  const trimmedValue = newValue.trim();
  hasSearchBeenMade.value = !!trimmedValue;
  isNoResultsFound.value = !!trimmedValue && providersFound.value.length === 0;
});

onBeforeMount(async () => {
  isLoading.value = false;

  sortProvider();
  const [providersList, gamesProvidersPage] = await Promise.all([
    storeSsGames.loadProviders(),
    storeSsGames.loadGamesForProvidersPage(),
    storeProviders.loadProviders(),
  ]);

  ssProviders.value = providersList;
  games.value = gamesProvidersPage;

  isLoading.value = true;
});
</script>

<style lang="scss" module src="./providersBlock.scss"></style>
